import { useCallback, useEffect, useState } from "react"

import { App, Col, Divider, Flex, Row, Space, Spin, Switch, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import TitleSection from "../../Components/TitleSection/TitleSection"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { userActions } from "../User/redux/user.slice"
import { userSave } from "../User/redux/user.thunks"

const { Text } = Typography

const statusList = [
  {
    id: "is_send_email_chargeback_prevented",
    title: "Chargeback prevented",
    text: "Receive a notification when a Chargeback is prevented."
  },
  /*{
    id: 'is_send_email_chargeback_invoice',
    title: 'Chargeback',
    text: 'Receive a notification when a Chargeback slips through.'
  },*/
  {
    id: "is_send_email_attention_required",
    title: "Attention required",
    text: "Receive a notification when an alert requires your attention."
  }
]

const summaryList = [
  {
    id: "is_send_email_weekly_summaries",
    title: "Weekly summaries",
    text: "Receive a notification for a weekly summary."
  },
  {
    id: "is_send_email_monthly_summaries",
    title: "Monthly summaries",
    text: "Receive a notification for a monthly summary."
  }
]

const creditsList = [
  {
    id: "is_send_email_credit_requests_daily_batch",
    title: "Daily batch of system credits requests",
    text: "Get a daily list of credit requests requiring proof."
  },
  {
    id: "is_send_email_credit_requests_weekly_batch",
    title: "Weekly batch of system credits requests",
    text: "Get a weekly list of credit requests requiring proof."
  },
  {
    id: "is_send_email_credit_request_approved",
    title: "Approvals",
    text: "Get an email when a credit is approved."
  },
  {
    id: "is_send_email_credit_request_rejected",
    title: "Rejections",
    text: "Get an email when a credit is rejected."
  }
]

const notificationsMap = {
  is_send_email_chargeback_prevented: "Chargeback prevented",
  is_send_email_chargeback_invoice: "Chargeback",
  is_send_email_attention_required: "Attention required",
  is_send_email_weekly_summaries: "Weekly summaries",
  is_send_email_monthly_summaries: "Monthly summaries",
  is_send_email_credit_requests_daily_batch: "Daily batch of system credits requests",
  is_send_email_credit_requests_weekly_batch: "Weekly batch of system credits requests",
  is_send_email_credit_request_approved: "Approvals",
  is_send_email_credit_request_rejected: "Rejections"
}

const Notifications = ({
  fetching,
  userInfo,
  isAdmin,
  userInfoForAdmin,
  saveFetching,
  saveResult,
  user_id,
  ...props
}) => {
  const currentUserInfo = isAdmin ? userInfoForAdmin : userInfo

  const { t } = useTranslation()
  const { notification } = App.useApp()
  const externalDemoMode = useExternalDemoMode()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [field, setField] = useState(null)
  const fieldId = field?.id

  // CHA1-2506: in demo, external demo and light mode the credit toggles are shown on and locked.
  const creditsLocked =
    externalDemoMode || !!currentUserInfo?.is_demo || !!currentUserInfo?.is_light_mode

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "490px" }
  }

  useEffect(() => {
    props.userSaveResultReset()
  }, [])

  useEffect(() => {
    if (saveResult && !saveResult?.message && fieldId && userInfo[fieldId] === field?.value) {
      notification.success({
        message: t("_notificationName_ were _newStatus_", {
          notificationName: t(notificationsMap[fieldId]),
          newStatus: t(!userInfo[fieldId] ? "turned off" : "turned on")
        }),
        description: t("_notificationName_ were _newStatus_ successfully.", {
          notificationName: t(notificationsMap[fieldId]),
          newStatus: t(!userInfo[fieldId] ? "turned off" : "turned on")
        }),
        ...notificationConfig
      })
      setField(null)
      props.userSaveResultReset()
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult, field, userInfo])

  const handleChange = useCallback(
    (checked, id) => {
      if (saveFetching || externalDemoMode) return
      setField({ id, value: checked })
      if (isAdmin && user_id) {
        props.updateUser({ [id]: checked, user_id })
      } else {
        props.updateUser({ [id]: checked })
      }
    },
    [externalDemoMode, saveFetching]
  )

  return (
    <div>
      <TitleSection
        icon="notification"
        title="Notifications"
        description="Enable or disable email notifications from us."
        style={{ margin: isMobile ? "-16px -16px 16px" : "-32px -32px 24px" }}
      />
      <Spin spinning={saveFetching}>
        <Row>
          <Col flex={"1 1 300px"}>
            <Space size={8} direction={"vertical"} style={{ paddingBottom: isMobile && 16 }}>
              <Text strong>{t("Status updates")}</Text>
              <Text type="secondary">
                {t(
                  `We'll email you about any dispute updates. Note: high dispute volume may cause noise.`
                )}
              </Text>
            </Space>
          </Col>
          <Col flex={"0 1 420px"}>
            <Flex gap={24} vertical={true}>
              {statusList.map(({ id, title, text }) => (
                <Space size={12} align="start" key={id}>
                  <Switch
                    checked={currentUserInfo?.[id]}
                    onChange={(checked) => handleChange(checked, id)}
                  />
                  <Space size={4} direction="vertical">
                    <Text>{t(title)}</Text>
                    <Text type="secondary">{t(text)}</Text>
                  </Space>
                </Space>
              ))}
            </Flex>
          </Col>
        </Row>

        <Divider style={{ visibility: isMobile && "hidden", margin: isMobile && "12px 0" }} />

        <Row>
          <Col flex={"1 1 300px"}>
            <Space size={8} direction={"vertical"} style={{ paddingBottom: isMobile && 16 }}>
              <Text strong>{t("Summary reports")}</Text>
              <Text type="secondary">
                {t("Adjust the summaries of your dispute progress over a set period.")}
              </Text>
            </Space>
          </Col>
          <Col flex={"0 1 420px"}>
            <Flex gap={24} vertical={true}>
              {summaryList.map(({ id, title, text }) => (
                <Space size={12} align="start" key={id}>
                  <Switch
                    checked={currentUserInfo?.[id]}
                    onChange={(checked) => handleChange(checked, id)}
                  />
                  <Space size={4} direction="vertical" style={{ padding: isMobile && "0" }}>
                    <Text>{t(title)}</Text>
                    <Text type="secondary">{t(text)}</Text>
                  </Space>
                </Space>
              ))}
            </Flex>
          </Col>
        </Row>

        <Divider style={{ visibility: isMobile && "hidden", margin: isMobile && "12px 0" }} />

        <Row>
          <Col flex={"1 1 300px"}>
            <Space size={8} direction={"vertical"} style={{ paddingBottom: isMobile && 16 }}>
              <Text strong>{t("Credit request updates")}</Text>
              <Text type="secondary">{t("Adjust your email preferences for credits.")}</Text>
            </Space>
          </Col>
          <Col flex={"0 1 420px"}>
            <Flex gap={24} vertical={true}>
              {creditsList.map(({ id, title, text }) => (
                <Space size={12} align="start" key={id}>
                  <Switch
                    checked={creditsLocked || !!currentUserInfo?.[id]}
                    disabled={creditsLocked}
                    onChange={(checked) => handleChange(checked, id)}
                  />
                  <Space size={4} direction="vertical" style={{ padding: isMobile && "0" }}>
                    <Text>{t(title)}</Text>
                    <Text type="secondary">{t(text)}</Text>
                  </Space>
                </Space>
              ))}
            </Flex>
          </Col>
        </Row>
      </Spin>
    </div>
  )
}

const mapStateToProps = (state) => ({
  fetching: state.auth.fetching,
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  userInfoForAdmin: state.user.userInfoForAdmin,

  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult
})

const mapDispatchToProps = (dispatch) => ({
  updateUser: (data) => dispatch(userSave(data)),
  userSaveResultReset: () => dispatch(userActions.userSaveResultReset())
})

export default connect(mapStateToProps, mapDispatchToProps)(Notifications)
